import { Payment, ApiResponse, type FeePreviewResult } from '@court-sync/shared';
import { apiGet, apiPost, publicApiGet } from './apiClient';

export interface CreatePaymentIntentData {
  amount: number;
  currency?: string;
  bookingId: string;
  customerEmail: string;
}

export interface PaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
  stripeConnectAccountId?: string;
}

export interface SetupIntentResponse {
  clientSecret: string;
  setupIntentId: string;
}

export type { FeePreviewResult };

export const paymentService = {
  async getPayments(filters?: {
    bookingId?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<Payment[]> & { pagination?: { page: number; limit: number; total: number; totalPages: number } }> {
    const params = filters || {};
    return apiGet<Payment[]>('/payments', { params }) as Promise<
      ApiResponse<Payment[]> & { pagination?: { page: number; limit: number; total: number; totalPages: number } }
    >;
  },

  async getPayment(id: string): Promise<ApiResponse<Payment>> {
    return apiGet<Payment>(`/payments/${id}`);
  },

  async createPaymentIntent(paymentData: CreatePaymentIntentData): Promise<ApiResponse<PaymentIntentResponse>> {
    return apiPost<PaymentIntentResponse>('/payments/create-intent', paymentData);
  },

  async validateCoupon(bookingId: string, code: string): Promise<ApiResponse<{ valid: boolean; amountOff?: number; error?: string }>> {
    return apiGet<{ valid: boolean; amountOff?: number; error?: string }>('/payments/validate-coupon', {
      params: { bookingId, code },
    });
  },

  async createLessonPayment(bookingId: string, couponCode?: string, bookingIds?: string[]): Promise<ApiResponse<PaymentIntentResponse & { amount?: number }>> {
    return apiPost<PaymentIntentResponse & { amount?: number }>('/payments/create-lesson-payment', {
      bookingId,
      couponCode: couponCode || undefined,
      ...(bookingIds && bookingIds.length > 1 && { bookingIds }),
    });
  },

  async confirmLessonPayment(paymentIntentId: string): Promise<ApiResponse<{ message: string }>> {
    return apiPost<{ message: string }>('/payments/confirm-lesson-payment', { paymentIntentId });
  },

  async processRefund(paymentId: string): Promise<ApiResponse<{ refundId: string }>> {
    return apiPost<{ refundId: string }>(`/payments/${paymentId}/refund`);
  },

  async refreshPaymentFees(paymentId: string): Promise<ApiResponse<Payment>> {
    return apiPost<Payment>(`/payments/${paymentId}/refresh-fees`);
  },

  async createPayment(paymentData: Partial<Payment>): Promise<ApiResponse<{ id: string }>> {
    return apiPost<{ id: string }>('/payments', paymentData);
  },

  async getFeePreviewPublic(bookingId: string, bookingIds?: string[]): Promise<ApiResponse<FeePreviewResult | null>> {
    const params: Record<string, string> = {};
    if (bookingIds && bookingIds.length > 1) params.bookingIds = bookingIds.join(',');
    return publicApiGet<FeePreviewResult | null>(
      `/public/payments/booking/${encodeURIComponent(bookingId)}/fee-preview`,
      { params }
    );
  },

  async getFeePreview(bookingId: string, couponCode?: string | null, bookingIds?: string[]): Promise<ApiResponse<FeePreviewResult | null>> {
    const params: Record<string, string> = {};
    if (couponCode && couponCode.trim()) params.couponCode = couponCode.trim();
    if (bookingIds && bookingIds.length > 1) params.bookingIds = bookingIds.join(',');
    return apiGet<FeePreviewResult | null>(`/payments/booking/${encodeURIComponent(bookingId)}/fee-preview`, { params });
  },

  async getReceiptUrl(paymentId: string): Promise<ApiResponse<{ receiptUrl: string }>> {
    return apiGet<{ receiptUrl: string }>(`/payments/${paymentId}/receipt`);
  },
};

export default paymentService;
